import React, { useState } from 'react';
import './styles.scss';
import { Link, useLocation } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { ArrowDownIcon } from 'cokitchen-icons';
import { ReactComponent as FoodCourtLogo } from 'assets/logo.svg';
import LogoutModal from 'components/modals/logout-modal';
import { getSidebarLinks } from 'utils/role-config';
import StoreSelectionDropdown from 'components/store-selection-dropdown';
import useRoleContext from 'contexts/role.context';

const Sidebar = () => {
  const { setShowMobileSidebar, activeStore, setShowStores, showStores } = useDashboardContext();
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null);
  const [activeSubmenuItems, setActiveSubmenuItems] = useState<string[]>([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { role, permissions, isMainAdmin } = useRoleContext();
  const { top: topMenu, bottom: bottomMenu } = role?.id
    ? getSidebarLinks(permissions, { isMainAdmin })
    : { top: [], bottom: [] };
  const location = useLocation();

  const toggleModal = () => {
    setShowLogoutModal(!showLogoutModal);
  };

  const LogOut = () => {
    setShowLogoutModal(true);
  };

  const handleDropdownClick = (label: string) => {
    setActiveMenuItem(activeMenuItem === label ? null : label);
  };

  const handleSubmenuClick = (label: string) => {
    setActiveSubmenuItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isLinkActive = (path: string) => {
    return location.pathname.includes(path);
  };

  const renderMenuItems = (menuItems: any[], parentPath = '', isNested?: boolean) => {
    return menuItems.map((item, index) => {
      const { name, path, dropdownList, icon: Icon } = item;
      const fullPath = parentPath ? `${parentPath}/${path}` : path;

      if (path) {
        return isNested ? (
          <>
            <li
              className={`sidebar_bottom_dropdown_item ${isLinkActive(path) ? 'is-active' : ''}`}
              onClick={() => setShowMobileSidebar(false)}>
              <Link to={path} className='block'>
                {name}
              </Link>
            </li>
          </>
        ) : (
          <Link
            key={index}
            to={path}
            onClick={() => setShowMobileSidebar(false)}
            className={`sidebar_bottom_item ${isLinkActive(path) ? 'active' : 'inactive'}`}>
            {Icon && (
              <div className={`icon ${isLinkActive(path) ? '!fill-red-5' : '!fill-gray-9'}`}>
                <Icon />
              </div>
            )}
            <p className='font-medium'>{name}</p>
          </Link>
        );
      }

      return (
        <div key={index}>
          <div
            onClick={() => handleDropdownClick(name)}
            className={`sidebar_bottom_item ${activeMenuItem === name ? 'active' : 'inactive'}`}>
            {Icon && (
              <div className={`icon ${activeMenuItem === name ? '!fill-red-5' : '!fill-gray-9'}`}>
                <Icon />
              </div>
            )}
            <div className='flex justify-between items-center w-full'>
              <p className='font-medium grow'>{name}</p>
              <ArrowDownIcon className='mr-4 text-[#6F7376]' />
            </div>
          </div>
          {activeMenuItem === name && dropdownList && (
            <div className='sidebar_bottom_item_dropdown'>
              <ul className='!pl-[32px]'>
                {dropdownList.map((subItem: any, subIndex: number) => (
                  <div key={subIndex}>
                    {subItem.path ? (
                      <div
                        className={`sidebar_bottom_dropdown_item ${
                          isLinkActive(subItem.path) ? 'is-active' : ''
                        }`}
                        onClick={() => setShowMobileSidebar(false)}>
                        <Link to={subItem.path} className='flex gap-2 items-center'>
                          {subItem.icon && (
                            <div
                              className={`icon ${
                                isLinkActive(subItem.path) ? '!fill-red-5' : '!fill-gray-9'
                              }`}>
                              <subItem.icon />
                            </div>
                          )}
                          {subItem.name}
                        </Link>
                      </div>
                    ) : (
                      <div>
                        <div
                          onClick={() => handleSubmenuClick(subItem.name)}
                          className={`sidebar_bottom_dropdown_item ${
                            activeSubmenuItems.includes(subItem.name) ? 'active' : ''
                          }`}>
                          <div className='flex justify-between items-center w-full'>
                            <div className='flex gap-2 items-center'>
                              {subItem.icon && (
                                <div
                                  className={`icon ${
                                    isLinkActive(subItem.path) ? '!fill-red-5' : '!fill-gray-9'
                                  }`}>
                                  <subItem.icon />
                                </div>
                              )}
                              <p className='font-medium'>{subItem.name}</p>
                            </div>
                            <ArrowDownIcon className='mr-8 text-[#6F7376]' />
                          </div>
                        </div>
                        {activeSubmenuItems.includes(subItem.name) && subItem.dropdownList && (
                          <ul className='!pl-[32px]'>
                            {renderMenuItems(subItem.dropdownList, fullPath, true)}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className='sidebar max-h-[90vh] min-h-[400px] flex flex-col justify-start items-start pb-6 w-full'>
      <div
        className={`lg:border-b border-[#EFF0F0] flex items-center -mx-4 lg:h-[80px] lg:min-h-[80px] w-full`}>
        <FoodCourtLogo className='ml-9' />
      </div>
      <div className=' w-full -mx-4 pl-9   '>
        <StoreSelectionDropdown show={showStores} setShow={setShowStores} />
      </div>
      {!showStores && activeStore?.id && (
        <div className='sidebar_bottom grow text-sm w-full'>{renderMenuItems(topMenu)}</div>
      )}
      <div className='sidebar_bottom text-sm w-full'>
        {bottomMenu.map(({ name, path }, index) =>
          path ? (
            <Link
              key={index}
              to={path}
              className={`sidebar_bottom_item ${isLinkActive(path) ? 'active' : 'inactive'}`}>
              <p className='font-medium'>{name}</p>
            </Link>
          ) : (
            <div key={index} className='sidebar_bottom_item inactive'>
              <p className='font-medium'>{name}</p>
            </div>
          )
        )}
        <div onClick={LogOut} className={`sidebar_bottom_item inactive`}>
          <p className='font-medium'>Logout</p>
        </div>
      </div>
      <LogoutModal onClose={toggleModal} show={showLogoutModal} />
    </div>
  );
};

export default Sidebar;
